/**
 * peer-chat.ts — "the peer" Anthropic-style NL chat channel renderer.
 *
 * Renders a claude.ai-style 1:1 chat UI inside the hub ecosystems server.
 * Mitchell talks plain English; "the peer" responds via the free AI pipeline
 * and emits structured JSON when Mitchell asks to build/change something.
 *
 * Layout: centred chat column (max-width 720px), user bubbles on the right,
 * assistant bubbles on the left, sticky compose at bottom. Code blocks get
 * Anthropic-style syntax highlighting.
 */

import { state } from '../state';
import { get, post } from '../api';
import { renderMarkdown } from './chat';

// ── Types ──────────────────────────────────────────────────────────

interface PeerMessage {
  id: string;
  server_id: string;
  channel: string;
  author_id: string;
  content: string;
  created_at: string;
  attachments: string;
  reactions: string;
  reply_to: string | null;
}

interface BuildAction {
  action: 'build';
  brief: string;
  surface: string;
}

interface PeerStatusResp {
  dev_n: number;
  status: string;
}

// ── Constants ──────────────────────────────────────────────────────

const BOT_ID = 'bot-the-peer';
const POLL_INTERVAL = 4000;

// ── Module state ───────────────────────────────────────────────────

let pollTimer: ReturnType<typeof setInterval> | null = null;
let statusPollTimer: ReturnType<typeof setInterval> | null = null;
let container: HTMLElement | null = null;
let scrollEl: HTMLElement | null = null;
let composeTextarea: HTMLTextAreaElement | null = null;

// ── Helpers ────────────────────────────────────────────────────────

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function fmtTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function isAtBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
}

function scrollToBottom(el: HTMLElement): void {
  el.scrollTop = el.scrollHeight;
}

// ── Build action detection ─────────────────────────────────────────

function extractBuildAction(content: string): BuildAction | null {
  // Look for JSON object on its own line or embedded in the response
  const patterns = [
    /\{[\s]*"action"[\s]*:[\s]*"build"[\s]*,[\s]*"brief"[\s]*:[\s]*"([^"]*)"[\s]*,[\s]*"surface"[\s]*:[\s]*"([^"]*)"[\s]*\}/,
  ];
  for (const pat of patterns) {
    const m = content.match(pat);
    if (m) {
      return { action: 'build', brief: m[1] ?? '', surface: m[2] ?? '' };
    }
  }
  // Also try full JSON parse for well-formed objects
  const jsonMatch = content.match(/\{[^{}]*"action"\s*:\s*"build"[^{}]*\}/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]) as Record<string, string>;
      if (parsed['action'] === 'build' && parsed['brief']) {
        return { action: 'build', brief: parsed['brief'], surface: parsed['surface'] ?? '' };
      }
    } catch { /* not valid JSON, skip */ }
  }
  return null;
}

function stripBuildJson(content: string): string {
  return content
    .replace(/\{[^{}]*"action"\s*:\s*"build"[^{}]*\}/g, '')
    .trim();
}

// ── Message rendering ──────────────────────────────────────────────

function renderMessage(msg: PeerMessage): HTMLElement {
  const isBot = msg.author_id === BOT_ID;
  const wrapper = document.createElement('div');
  wrapper.className = `peer-msg ${isBot ? 'peer-msg-assistant' : 'peer-msg-user'}`;
  wrapper.dataset['id'] = msg.id;

  const bubble = document.createElement('div');
  bubble.className = `peer-bubble ${isBot ? 'peer-bubble-assistant' : 'peer-bubble-user'}`;

  const action = isBot ? extractBuildAction(msg.content) : null;
  const displayContent = action ? stripBuildJson(msg.content) : msg.content;

  // Author label (only for assistant)
  if (isBot) {
    const label = document.createElement('div');
    label.className = 'peer-author';
    label.textContent = 'the peer';
    bubble.appendChild(label);
  }

  // Content
  const contentEl = document.createElement('div');
  contentEl.className = 'peer-content';
  contentEl.innerHTML = renderMarkdown(displayContent || (action ? '' : msg.content));
  bubble.appendChild(contentEl);

  // Timestamp
  const time = document.createElement('div');
  time.className = 'peer-time';
  time.textContent = fmtTime(msg.created_at);
  bubble.appendChild(time);

  wrapper.appendChild(bubble);

  // Build action confirmation card
  if (action) {
    const card = renderConfirmCard(action, msg.id);
    wrapper.appendChild(card);
  }

  return wrapper;
}

function renderConfirmCard(action: BuildAction, msgId: string): HTMLElement {
  const card = document.createElement('div');
  card.className = 'peer-confirm-card';
  card.dataset['msgId'] = msgId;

  const header = document.createElement('div');
  header.className = 'peer-confirm-header';
  header.textContent = "Here's what I'd ask a dev to do — does this look right?";
  card.appendChild(header);

  const briefEl = document.createElement('div');
  briefEl.className = 'peer-confirm-brief';
  briefEl.innerHTML = renderMarkdown(action.brief);
  card.appendChild(briefEl);

  if (action.surface) {
    const surface = document.createElement('div');
    surface.className = 'peer-confirm-surface';
    surface.textContent = `Surface: ${action.surface}`;
    card.appendChild(surface);
  }

  const btns = document.createElement('div');
  btns.className = 'peer-confirm-btns';

  const yesBtn = document.createElement('button');
  yesBtn.className = 'peer-btn peer-btn-primary';
  yesBtn.textContent = 'Yes';
  yesBtn.addEventListener('click', () => void handleDispatch(action, card));

  const editBtn = document.createElement('button');
  editBtn.className = 'peer-btn peer-btn-secondary';
  editBtn.textContent = 'Edit';
  editBtn.addEventListener('click', () => handleEdit(action));

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'peer-btn peer-btn-ghost';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.addEventListener('click', () => {
    card.remove();
  });

  btns.appendChild(yesBtn);
  btns.appendChild(editBtn);
  btns.appendChild(cancelBtn);
  card.appendChild(btns);

  return card;
}

async function handleDispatch(action: BuildAction, card: HTMLElement): Promise<void> {
  // Replace card with "dispatching..." status
  card.innerHTML = '<div class="peer-dispatching">Dispatching to dev pipeline...</div>';

  try {
    const resp = await post<{ status: string; brief_path: string; message: string }>(
      '/api/peer/dispatch',
      { brief: action.brief, surface: action.surface },
    );
    card.innerHTML = `<div class="peer-dispatch-result"><div class="peer-dispatch-status">${esc(resp.message)}</div>${resp.brief_path ? `<div class="peer-dispatch-path">${esc(resp.brief_path)}</div>` : ''}</div>`;
  } catch (e) {
    card.innerHTML = `<div class="peer-dispatch-result peer-dispatch-error">Dispatch failed: ${esc(String(e))}</div>`;
  }
}

function handleEdit(action: BuildAction): void {
  // Put the brief text into the compose box for editing
  if (composeTextarea) {
    composeTextarea.value = `Build: ${action.brief}\nSurface: ${action.surface}`;
    composeTextarea.focus();
    autoGrow(composeTextarea);
  }
}

// ── Status pill ────────────────────────────────────────────────────

function renderStatusPill(devN: number): HTMLElement {
  const pill = document.createElement('div');
  pill.className = 'peer-status-pill';
  pill.id = 'peer-status-pill';
  pill.innerHTML = `<div class="peer-status-dot"></div><span>the peer is working on this — dev${devN} running</span><div class="peer-status-bar"><div class="peer-status-bar-fill"></div></div>`;
  return pill;
}

/** Start polling dev<N> done flag. Exported for future auto-dispatch wiring. */
export function startStatusPoll(devN: number): void {
  stopStatusPoll();

  const pill = document.getElementById('peer-status-pill');
  if (!pill && container) {
    const headerArea = container.querySelector('.peer-header');
    if (headerArea) {
      headerArea.appendChild(renderStatusPill(devN));
    }
  }

  statusPollTimer = setInterval(() => {
    void (async () => {
      try {
        const resp = await get<PeerStatusResp>(`/api/peer/status/${devN}`);
        if (resp.status === 'done') {
          const p = document.getElementById('peer-status-pill');
          if (p) {
            p.classList.add('peer-status-done');
            p.querySelector('span')!.textContent = `dev${devN} finished!`;
          }
          stopStatusPoll();
        }
      } catch { /* ignore poll errors */ }
    })();
  }, 5000);
}

function stopStatusPoll(): void {
  if (statusPollTimer) {
    clearInterval(statusPollTimer);
    statusPollTimer = null;
  }
}

// ── Compose ────────────────────────────────────────────────────────

function autoGrow(textarea: HTMLTextAreaElement): void {
  textarea.style.height = 'auto';
  const max = Math.max(120, window.innerHeight * 0.3);
  textarea.style.height = `${Math.min(textarea.scrollHeight, max)}px`;
}

async function sendMessage(content: string): Promise<void> {
  if (!content.trim()) return;

  const serverId = state.currentServerId ?? state.currentServer;
  if (!serverId) return;

  try {
    await post('/api/messages/send', {
      server_id: serverId,
      channel: 'the-peer',
      content: content.trim(),
    });
  } catch (e) {
    // Show error inline
    if (scrollEl) {
      const errEl = document.createElement('div');
      errEl.className = 'peer-msg peer-msg-error';
      errEl.textContent = `Failed to send: ${String(e)}`;
      const msgsEl = scrollEl.querySelector('.peer-messages');
      if (msgsEl) msgsEl.appendChild(errEl);
    }
  }
}

// ── Message polling ────────────────────────────────────────────────

const seenIds = new Set<string>();

async function loadMessages(): Promise<void> {
  const serverId = state.currentServerId ?? state.currentServer;
  if (!serverId || !scrollEl) return;

  try {
    const msgs = await get<PeerMessage[]>(
      `/api/messages?server_id=${encodeURIComponent(serverId)}&channel=the-peer&limit=50`,
    );

    const msgsEl = scrollEl.querySelector('.peer-messages');
    if (!msgsEl) return;

    const wasBottom = isAtBottom(scrollEl);
    let added = false;

    for (const msg of msgs) {
      if (seenIds.has(msg.id)) continue;
      seenIds.add(msg.id);
      msgsEl.appendChild(renderMessage(msg));

      added = true;
    }

    if (added && wasBottom) {
      scrollToBottom(scrollEl);
    }
  } catch { /* silent on poll fail */ }
}

function startPolling(): void {
  stopPolling();
  void loadMessages();
  pollTimer = setInterval(() => void loadMessages(), POLL_INTERVAL);
}

function stopPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

// ── Main renderer ──────────────────────────────────────────────────

export function renderPeerChat(el: HTMLElement, _channelName: string): void {
  cleanupPeerChat();
  container = el;
  seenIds.clear();

  el.innerHTML = '';
  el.className = 'peer-chat-root';

  // Header
  const header = document.createElement('div');
  header.className = 'peer-header';
  header.innerHTML = `<div class="peer-header-title"><span class="peer-header-name">the peer</span><span class="peer-header-desc">Talk plain — build things</span></div>`;
  el.appendChild(header);

  // Scrollable message area
  const scroll = document.createElement('div');
  scroll.className = 'peer-scroll';
  scrollEl = scroll;

  const msgsWrap = document.createElement('div');
  msgsWrap.className = 'peer-messages-wrap';

  const msgs = document.createElement('div');
  msgs.className = 'peer-messages';
  msgsWrap.appendChild(msgs);

  scroll.appendChild(msgsWrap);
  el.appendChild(scroll);

  // Compose bar
  const compose = document.createElement('div');
  compose.className = 'peer-compose';

  const composeInner = document.createElement('div');
  composeInner.className = 'peer-compose-inner';

  const textarea = document.createElement('textarea');
  textarea.className = 'peer-textarea';
  textarea.placeholder = 'Message the peer...';
  textarea.rows = 1;
  composeTextarea = textarea;

  textarea.addEventListener('input', () => autoGrow(textarea));
  textarea.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const val = textarea.value;
      textarea.value = '';
      autoGrow(textarea);
      void sendMessage(val);
    }
  });

  const sendBtn = document.createElement('button');
  sendBtn.className = 'peer-send-btn';
  sendBtn.setAttribute('aria-label', 'Send');
  sendBtn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>';
  sendBtn.addEventListener('click', () => {
    const val = textarea.value;
    textarea.value = '';
    autoGrow(textarea);
    void sendMessage(val);
  });

  composeInner.appendChild(textarea);
  composeInner.appendChild(sendBtn);
  compose.appendChild(composeInner);
  el.appendChild(compose);

  // Start loading
  startPolling();
  textarea.focus();
}

export function cleanupPeerChat(): void {
  stopPolling();
  stopStatusPoll();
  container = null;
  scrollEl = null;
  composeTextarea = null;
  seenIds.clear();
}
